import {
  apiClient,
  ApiError,
  ApiResponse,
  PaginatedApiResponse,
} from "@/lib/apiClient";
import {
  Page,
  CreatePageRequest,
  CreatePageDraftRequest,
  UpdatePageDraftRequest,
  PageDraft,
  PageDraftResponse,
  Section,
  SectionDataForDraft,
} from "@/types/models/page";

/**
 * Sanitizes section IDs in a page object by setting the id field to a nil UUID
 * for any section with an ID starting with 'temp-'. This is needed for proper
 * server-side handling of unpublished/new sections.
 *
 * @param page The page object to sanitize
 * @returns A new page object with sanitized section IDs
 */
const sanitizeSectionIds = <T extends { sections?: Section[] }>(page: T): T => {
  // If there are no sections, return the original page
  if (!page.sections || page.sections.length === 0) {
    return page;
  }

  // Create a deep clone of the page object to avoid mutation
  const sanitizedPage = JSON.parse(JSON.stringify(page)) as T;

  // Process each section and set id to nil UUID if it starts with 'temp-'
  sanitizedPage.sections = sanitizedPage.sections?.map((section) => {
    if (section.id && section.id.startsWith("temp-")) {
      return {
        ...section,
        id: "00000000-0000-0000-0000-000000000000", // Set to nil UUID for sections with temporary IDs
      };
    }
    return section;
  });

  return sanitizedPage;
};

/**
 * Sanitizes section IDs in a draft request by setting the section_id field to a nil UUID
 * (00000000-0000-0000-0000-000000000000) for any section with an ID starting with 'temp-'.
 * This ensures proper server-side handling of new/unpublished sections.
 *
 * @param draftData The draft request object to sanitize
 * @returns A new draft request object with sanitized section IDs
 */
const sanitizeDraftSectionIds = <
  T extends { sections?: SectionDataForDraft[] },
>(
  draftData: T,
): T => {
  // If there are no sections, return the original data
  if (!draftData.sections || draftData.sections.length === 0) {
    return draftData;
  }

  // Create a deep clone of the draft data to avoid mutation
  const sanitizedDraftData = JSON.parse(JSON.stringify(draftData)) as T;

  // Process each section and set section_id to nil UUID if it starts with 'temp-'
  sanitizedDraftData.sections = sanitizedDraftData.sections?.map((section) => {
    if (section.section_id && section.section_id.startsWith("temp-")) {
      return {
        ...section,
        section_id: "00000000-0000-0000-0000-000000000000", // Set to nil UUID for sections with temporary IDs
      };
    }
    return section;
  });

  return sanitizedDraftData;
};

export const getPages = async ({
  page,
  pageSize,
}: {
  page: number;
  pageSize: number;
}): Promise<PaginatedApiResponse<Page[]>> => {
  try {
    const data: PaginatedApiResponse<Page[]> = await apiClient.getPaginated(
      "/api/v1/admin/:storeId/web-builder/pages",
      {
        page: page.toString(),
        page_size: pageSize.toString(),
      },
    );
    return data;
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
  }
};

export const getPage = async ({
  pageId,
}: {
  pageId: string;
}): Promise<ApiResponse<Page>> => {
  try {
    const response: ApiResponse<Page> = await apiClient.get(
      `/api/v1/admin/:storeId/web-builder/pages/${pageId}`,
    );
    return {
      data: response.data,
      status: response.status,
      message: response.message,
      success: response.success,
      valid: response.valid,
    };
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
  }
};

/** POST body must match backend CreatePageRequest — omit empty UUID-string fields. */
function buildCreatePagePayload(data: Page): Record<string, unknown> {
  const sanitizedData = sanitizeSectionIds(data);
  const body: Record<string, unknown> = {
    name: sanitizedData.name,
    title: sanitizedData.title,
    slug: sanitizedData.slug,
    description: sanitizedData.description ?? "",
    status: sanitizedData.status || "draft",
  };

  const assetId = sanitizedData.featured_image_asset_id;
  if (assetId != null && String(assetId).trim() !== "") {
    body.featured_image_asset_id = assetId;
  }

  const templateId = sanitizedData.template_id;
  if (templateId != null && String(templateId).trim() !== "") {
    body.template_id = templateId;
  }

  const templatePageId = (sanitizedData as Page & { template_page_id?: string })
    .template_page_id;
  if (templatePageId != null && String(templatePageId).trim() !== "") {
    body.template_page_id = templatePageId;
  }

  return body;
}

export const postPage = async ({
  data,
}: {
  data: Page;
}): Promise<ApiResponse<Page>> => {
  try {
    const payload = buildCreatePagePayload(data);

    const response: ApiResponse<Page> = await apiClient.post(
      `/api/v1/admin/:storeId/web-builder/pages`,
      payload,
    );
    return {
      data: response.data,
      status: response.status,
      message: response.message,
      success: response.success,
      valid: response.valid,
    };
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
  }
};

export const putPage = async ({
  pageId,
  data,
}: {
  pageId: string;
  data: Partial<Page>;
}): Promise<ApiResponse<Page>> => {
  try {
    // Sanitize section IDs before sending to the server
    const sanitizedData = sanitizeSectionIds(data);

    const response: ApiResponse<Page> = await apiClient.put(
      `/api/v1/admin/:storeId/web-builder/pages/${pageId}`,
      sanitizedData,
    );
    return {
      data: response.data,
      status: response.status,
      message: response.message,
      success: response.success,
      valid: response.valid,
    };
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
  }
};

export const deletePage = async ({
  pageId,
}: {
  pageId: string;
}): Promise<ApiResponse<void>> => {
  try {
    const response: ApiResponse<void> = await apiClient.delete(
      `/api/v1/admin/:storeId/web-builder/pages/${pageId}`,
    );
    return {
      data: response.data,
      status: response.status,
      message: response.message,
      success: response.success,
      valid: response.valid,
    };
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
  }
};

export const createPagesFromTemplate = async ({
  templateId,
  pageIds,
}: {
  templateId: string;
  pageIds?: string[];
}): Promise<ApiResponse<Page[]>> => {
  try {
    const requestBody = pageIds ? { page_ids: pageIds } : {};
    const response: ApiResponse<Page[]> = await apiClient.post(
      `/api/v1/admin/:storeId/web-builder/pages/from-template/${templateId}`,
      requestBody,
    );
    return {
      data: response.data,
      status: response.status,
      message: response.message,
      success: response.success,
      valid: response.valid,
    };
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
  }
};

export const createPageFromTemplate = async ({
  pageData,
}: {
  pageData: CreatePageRequest;
}): Promise<ApiResponse<Page>> => {
  try {
    const response: ApiResponse<Page> = await apiClient.post(
      `/api/v1/admin/:storeId/web-builder/pages`,
      pageData,
    );
    return {
      data: response.data,
      status: response.status,
      message: response.message,
      success: response.success,
      valid: response.valid,
    };
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
  }
};

// Draft-related API actions
export const getPageDrafts = async ({
  pageId,
}: {
  pageId: string;
}): Promise<ApiResponse<PageDraft[]>> => {
  try {
    const response: ApiResponse<PageDraft[]> = await apiClient.get(
      `/api/v1/admin/:storeId/web-builder/pages/${pageId}/drafts`,
    );
    return {
      data: response.data,
      status: response.status,
      message: response.message,
      success: response.success,
      valid: response.valid,
    };
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
  }
};

export const createPageDraft = async ({
  pageId,
  draftData,
}: {
  pageId: string;
  draftData: CreatePageDraftRequest;
}): Promise<ApiResponse<PageDraftResponse>> => {
  try {
    // Sanitize section IDs before sending to the server
    const sanitizedDraftData = sanitizeDraftSectionIds(draftData);

    const response: ApiResponse<PageDraftResponse> = await apiClient.post(
      `/api/v1/admin/:storeId/web-builder/pages/${pageId}/drafts`,
      sanitizedDraftData,
    );
    return {
      data: response.data,
      status: response.status,
      message: response.message,
      success: response.success,
      valid: response.valid,
    };
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
  }
};

export const getPageDraft = async ({
  pageId,
  draftId,
}: {
  pageId: string;
  draftId: string;
}): Promise<ApiResponse<PageDraftResponse>> => {
  try {
    const response: ApiResponse<PageDraftResponse> = await apiClient.get(
      `/api/v1/admin/:storeId/web-builder/pages/${pageId}/drafts/${draftId}`,
    );
    return {
      data: response.data,
      status: response.status,
      message: response.message,
      success: response.success,
      valid: response.valid,
    };
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
  }
};

export const updatePageDraft = async ({
  pageId,
  draftId,
  draftData,
}: {
  pageId: string;
  draftId: string;
  draftData: UpdatePageDraftRequest;
}): Promise<ApiResponse<PageDraftResponse>> => {
  try {
    // Sanitize section IDs before sending to the server
    const sanitizedDraftData = sanitizeDraftSectionIds(draftData);

    const response: ApiResponse<PageDraftResponse> = await apiClient.put(
      `/api/v1/admin/:storeId/web-builder/pages/${pageId}/drafts/${draftId}`,
      sanitizedDraftData,
    );
    return {
      data: response.data,
      status: response.status,
      message: response.message,
      success: response.success,
      valid: response.valid,
    };
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
  }
};

export const deletePageDraft = async ({
  pageId,
  draftId,
}: {
  pageId: string;
  draftId: string;
}): Promise<ApiResponse<void>> => {
  try {
    const response: ApiResponse<void> = await apiClient.delete(
      `/api/v1/admin/:storeId/web-builder/pages/${pageId}/drafts/${draftId}`,
    );
    return {
      data: response.data,
      status: response.status,
      message: response.message,
      success: response.success,
      valid: response.valid,
    };
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
  }
};

export const publishPageDraft = async ({
  pageId,
  draftId,
}: {
  pageId: string;
  draftId: string;
}): Promise<ApiResponse<PageDraftResponse>> => {
  try {
    const response: ApiResponse<PageDraftResponse> = await apiClient.post(
      `/api/v1/admin/:storeId/web-builder/pages/${pageId}/drafts/${draftId}/publish`,
    );
    return {
      data: response.data,
      status: response.status,
      message: response.message,
      success: response.success,
      valid: response.valid,
    };
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
  }
};
